import { useMemo, type ReactNode } from 'react';

import LucideSymbolIcon from '../icons/LucideSymbolIcon';
import {
  EXERCISE_QUESTION_TYPE_LABELS,
  jsonToExerciseQuestions,
  type ExerciseQuestion,
} from './ElearningExerciseEditor';

type Props = {
  questions: ExerciseQuestion[];
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
};

export function ElearningQuizCardShell({
  title,
  meta,
  notices,
  children,
  footer,
}: {
  title: string;
  meta: string;
  notices?: Array<{ text: string; variant: 'warning' | 'success' }>;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <section className="elearning-quiz-card" aria-label={title}>
      <div className="elearning-quiz-card__accent" aria-hidden />
      <header className="elearning-quiz-card__header">
        <span className="elearning-quiz-card__icon" aria-hidden>
          <LucideSymbolIcon name="edit_note" tone="inherit" size={22} />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="elearning-quiz-card__title">{title}</h2>
          <p className="elearning-quiz-card__meta">{meta}</p>
        </div>
      </header>
      {notices?.map((notice) => (
        <p
          key={notice.text}
          className={`elearning-quiz-card__notice elearning-quiz-card__notice--${notice.variant}`}
        >
          {notice.text}
        </p>
      ))}
      <div className="elearning-quiz-card__body">{children}</div>
      {footer ? <footer className="elearning-quiz-card__footer">{footer}</footer> : null}
    </section>
  );
}

function quizMeta(questions: ExerciseQuestion[]) {
  const totalPts = questions.reduce((sum, q) => sum + (q.points ?? 1), 0);
  const qLabel = `${questions.length} question${questions.length > 1 ? 's' : ''}`;
  const pLabel = `${totalPts} point${totalPts > 1 ? 's' : ''}`;
  return `${qLabel} · ${pLabel}`;
}

function ClassementAnswer({
  question,
  value,
  onChange,
}: {
  question: ExerciseQuestion;
  value: string[];
  onChange: (next: string[]) => void;
}) {
  const items = value.length > 0 ? value : [...(question.items ?? question.ordreCorrect ?? [])];
  let dragIndex: number | null = null;

  function reorder(from: number, to: number) {
    if (from === to) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next);
  }

  return (
    <div>
      <p className="elearning-quiz-rank-hint">Glissez les éléments pour les classer dans le bon ordre.</p>
      <div className="elearning-quiz-options">
        {items.map((item, index) => (
          <div
            key={`${question.id}-${index}-${item}`}
            draggable
            onDragStart={() => {
              dragIndex = index;
            }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => {
              if (dragIndex != null) reorder(dragIndex, index);
              dragIndex = null;
            }}
            className="elearning-quiz-rank-item"
          >
            <span className="elearning-quiz-rank-item__index">{index + 1}</span>
            {item}
          </div>
        ))}
      </div>
    </div>
  );
}

function QuestionBlock({
  question,
  index,
  value,
  onChange,
}: {
  question: ExerciseQuestion;
  index: number;
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
}) {
  const type = question.type ?? 'QCM';
  const typeLabel = EXERCISE_QUESTION_TYPE_LABELS[type] ?? type;
  const points = question.points ?? 1;

  return (
    <article className="elearning-quiz-question">
      <div className="elearning-quiz-question__head">
        <span className="elearning-quiz-question__index">{index + 1}</span>
        <span className="elearning-quiz-question__type">{typeLabel}</span>
        <span className="elearning-quiz-question__points">
          {points} pt{points > 1 ? 's' : ''}
        </span>
      </div>
      <p className="elearning-quiz-question__label">{question.libelle}</p>

      {type === 'QCM' ? (
        <div className="elearning-quiz-options mt-3">
          {(question.options ?? []).map((opt) => {
            const selected = value[question.id] === opt;
            return (
              <label
                key={opt}
                className={`elearning-quiz-option ${selected ? 'elearning-quiz-option--selected' : ''}`}
              >
                <input
                  type="radio"
                  className="elearning-quiz-option__input"
                  name={question.id}
                  value={opt}
                  checked={selected}
                  onChange={() => onChange({ ...value, [question.id]: opt })}
                />
                <span className="elearning-quiz-option__bullet" aria-hidden />
                <span>{opt}</span>
              </label>
            );
          })}
        </div>
      ) : null}

      {type === 'VRAI_FAUX' ? (
        <div className="elearning-quiz-tf mt-3">
          {[
            { label: 'Vrai', val: true },
            { label: 'Faux', val: false },
          ].map(({ label, val }) => (
            <button
              key={label}
              type="button"
              className={`elearning-quiz-tf__btn ${value[question.id] === val ? 'elearning-quiz-tf__btn--selected' : ''}`}
              onClick={() => onChange({ ...value, [question.id]: val })}
            >
              {label}
            </button>
          ))}
        </div>
      ) : null}

      {type === 'TEXTE_LIBRE' ? (
        <textarea
          className="elearning-quiz-textarea academia-field mt-3"
          rows={4}
          value={String(value[question.id] ?? '')}
          onChange={(e) => onChange({ ...value, [question.id]: e.target.value })}
          placeholder="Saisissez votre réponse ici…"
        />
      ) : null}

      {type === 'CLASSEMENT' ? (
        <div className="mt-3">
          <ClassementAnswer
            question={question}
            value={
              Array.isArray(value[question.id]) && (value[question.id] as string[]).length > 0
                ? (value[question.id] as string[])
                : [...(question.items ?? question.ordreCorrect ?? [])]
            }
            onChange={(next) => onChange({ ...value, [question.id]: next })}
          />
        </div>
      ) : null}
    </article>
  );
}

export default function ElearningExercisePlayer({ questions, value, onChange }: Props) {
  return (
    <div className="elearning-quiz-questions">
      {questions.map((q, index) => (
        <QuestionBlock key={q.id} question={q} index={index} value={value} onChange={onChange} />
      ))}
    </div>
  );
}

export function useExerciseQuestionsFromJson(contenuJson: unknown) {
  return useMemo(() => jsonToExerciseQuestions(contenuJson), [contenuJson]);
}

export function formatQuizMeta(questions: ExerciseQuestion[]) {
  return quizMeta(questions);
}
